import { assertNever } from '../utils/assert-never';

/** Backend error code for Coreplus appointment slot conflict (HTTP 409). */
export const APPOINTMENT_CONFLICT_ERROR_CODE = 'APPOINTMENT_CONFLICT';

/** Backend error code when identity does not match any workflow (HTTP 404). */
export const PATIENT_NOT_FOUND_ERROR_CODE = 'PATIENT_NOT_FOUND';

/** Backend error code when several patient records share the submitted name and date of birth (HTTP 409). */
export const DUPLICATE_PATIENT_ERROR_CODE = 'DUPLICATE_PATIENT';

/** Patient copy for DUPLICATE_PATIENT: retrying cannot help, so point them to the clinic. */
export const DUPLICATE_PATIENT_MESSAGE = 'We found more than one record with that name and date of birth, so we '
  + 'couldn\'t save your answers automatically. Please contact our team and '
  + 'we\'ll match your answers to the right record.';

/** Backend error code when the shown fee no longer matches the server price (HTTP 409). */
export const FEE_CHANGED_ERROR_CODE = 'FEE_CHANGED';

/** Shown when fee inputs are incomplete so estimate cannot be computed. */
export const FEE_ESTIMATE_MISSING_MESSAGE = 'Fee estimate is missing';

export type SubmissionFailure =
  | { kind: 'appointment-conflict' }
  | { kind: 'patient-not-found' }
  | { kind: 'duplicate-patient' }
  | { kind: 'fee-changed' }
  | { kind: 'slot-expired' }
  | { kind: 'date-unavailable' }
  | { kind: 'validation'; message: string }
  | { kind: 'unknown'; message: string };

export type SubmissionState =
  | { status: 'idle' }
  | { status: 'submitting' }
  | { status: 'success' }
  | { status: 'error'; failure: SubmissionFailure };

export type SubmissionAction =
  | { type: 'SUBMIT_STARTED' }
  | { type: 'SUBMIT_SUCCEEDED' }
  | { type: 'SUBMIT_FAILED'; failure: SubmissionFailure }
  | { type: 'RESET_ERROR' };

export const initialSubmissionState: SubmissionState = { status: 'idle' };

export const submissionReducer = (
  previous: SubmissionState,
  action: SubmissionAction,
): SubmissionState => {
  switch (action.type) {
    case 'SUBMIT_STARTED':
      return { status: 'submitting' };
    case 'SUBMIT_SUCCEEDED':
      return { status: 'success' };
    case 'SUBMIT_FAILED':
      return { status: 'error', failure: action.failure };
    case 'RESET_ERROR':
      return previous.status === 'error' ? { status: 'idle' } : previous;
    default:
      return assertNever(action, 'Unexpected submission action');
  }
};

export const toFailure = (
  errorCode: string | undefined,
  message: string,
): SubmissionFailure => {
  if (errorCode === APPOINTMENT_CONFLICT_ERROR_CODE) {
    return { kind: 'appointment-conflict' };
  }
  if (errorCode === PATIENT_NOT_FOUND_ERROR_CODE) {
    return { kind: 'patient-not-found' };
  }
  if (errorCode === DUPLICATE_PATIENT_ERROR_CODE) {
    return { kind: 'duplicate-patient' };
  }
  if (errorCode === FEE_CHANGED_ERROR_CODE) {
    return { kind: 'fee-changed' };
  }
  return { kind: 'unknown', message };
};
